import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

@Component({
  selector: 'app-terms-conditions',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <div class="min-h-screen bg-polished-charcoal text-silk-white py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-4xl mx-auto space-y-8">
        
        <!-- Barra de Navegación Legal Superior (Tabs UX) -->
        <div class="bg-black/40 backdrop-blur-md p-2 rounded-2xl border border-white/10 flex flex-wrap gap-2 justify-center sm:justify-between items-center text-xs sm:text-sm">
          <a routerLink="/" class="text-amethyst-glint hover:text-white px-3 py-2 rounded-xl transition flex items-center gap-1.5 font-medium">
            ← Inicio
          </a>
          <div class="flex flex-wrap gap-1 sm:gap-2">
            <a routerLink="/terminos" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              ⚖️ Términos
            </a>
            <a routerLink="/privacidad" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🔒 Privacidad
            </a>
            <a routerLink="/garantias-retracto" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🛡️ Garantías
            </a>
            <a routerLink="/aviso-legal" routerLinkActive="bg-violet-600/30 text-white border-violet-500/50" class="px-3 py-1.5 rounded-xl border border-transparent text-silk-white/80 hover:text-white hover:bg-white/5 transition">
              🏛️ Marcas
            </a>
          </div>
          <button (click)="printPage()" class="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-silk-white/80 hover:text-white transition text-xs border border-white/10">
            🖨️ Imprimir
          </button>
        </div>

        <!-- Tarjeta Principal de Contenido -->
        <div class="bg-black/30 backdrop-blur-md rounded-3xl shadow-2xl p-8 sm:p-12 border border-white/10">
          
          <div class="flex items-center justify-between border-b border-white/10 pb-6 mb-8">
            <div>
              <span class="text-xs text-amethyst-glint uppercase tracking-widest font-bold">Centro Jurídico • Colombia</span>
              <h1 class="text-3xl sm:text-4xl font-extrabold text-white mt-1 tracking-tight">
                Términos y Condiciones de Uso
              </h1>
            </div>
            <div class="text-right hidden sm:block">
              <p class="text-xs text-silk-white/50">Actualización</p>
              <p class="text-sm font-semibold text-amethyst-glint">Noviembre 2026</p>
            </div>
          </div>

          <p class="text-sm text-silk-white/60 mb-8 bg-white/5 p-4 rounded-xl border border-white/5">
            📜 Conforme a la <strong>Ley 1480 de 2011 (Estatuto del Consumidor)</strong> y regulaciones aplicables al comercio electrónico en la República de Colombia.
          </p>

          <div class="space-y-8 text-silk-white/90 leading-relaxed text-sm sm:text-base">
            
            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>1.</span> Identificación del Titular y Objeto
              </h2>
              <p class="text-silk-white/80">
                Bienvenido a <strong>Altair Parfum</strong> (<a href="https://altairparfum.com" class="text-cyan-400 underline">altairparfum.com</a>), establecimiento comercial operando en Bogotá D.C., Colombia, especializado en perfumería árabe de lujo original y servicios de fraccionamiento artesanal (decants de 5ml y 10ml) y botellas completas. Las presentes condiciones regulan el uso de la plataforma y la relación contractual con el consumidor.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>2.</span> Aceptación de los Términos
              </h2>
              <p class="text-silk-white/80">
                El acceso, navegación y realización de pedidos en altairparfum.com implican la lectura, conocimiento y aceptación expresa y sin reservas de estos Términos y Condiciones, así como de nuestra Política de Privacidad.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>3.</span> Capacidad Legal y Cobertura
              </h2>
              <p class="text-silk-white/80">
                Servicios dirigidos a personas mayores de edad con capacidad legal en Colombia. Cobertura nacional mediante operadores logísticos aliados y opciones de pago contra entrega en zonas habilitadas.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>4.</span> Productos, Precios y Transparencia Fiscal
              </h2>
              <ul class="list-disc pl-6 space-y-2 text-silk-white/80">
                <li><strong>Originalidad:</strong> 100% auténticos, provenientes de botellas originales importadas legítimamente.</li>
                <li><strong>Fraccionamiento Artesanal:</strong> Decants preparados bajo estrictos estándares de higiene en viales de vidrio con sellado hermético.</li>
                <li><strong>Precios y Transparencia:</strong> Los precios publicados están expresados en pesos colombianos (COP) y corresponden al precio total final a pagar. <strong>Altair Parfum opera bajo el régimen no responsable de IVA</strong>. Los costos de envío se calculan y comunican antes de confirmar la orden.</li>
              </ul>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>5.</span> Modalidades de Pago y Pago Contra Entrega
              </h2>
              <p class="text-silk-white/80">
                Ofrecemos pasarelas digitales seguras y la modalidad de <strong>Pago Contra Entrega</strong>. Al elegir pago contra entrega, el cliente adquiere un compromiso firme de recepción y pago al transportador autorizado.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>6.</span> Propiedad Intelectual y Marcas
              </h2>
              <p class="text-silk-white/80">
                Las marcas mencionadas (Lattafa, Maison Alhambra, Afnan, Armaf, etc.) son propiedad de sus titulares. Su mención es <strong>estrictamente descriptiva e informativa</strong> para identificar la fragancia original en nuestros decants. Altair Parfum es independiente y no tiene exclusividad ni representación oficial con los fabricantes.
              </p>
            </section>

            <section class="space-y-3">
              <h2 class="text-xl font-bold text-white flex items-center gap-2 border-l-4 border-amethyst-glint pl-3">
                <span>7.</span> Atención al Consumidor y PQR
              </h2>
              <div class="p-5 bg-black/40 rounded-xl border border-white/10 space-y-2 text-sm">
                <p>📧 <strong>Correo:</strong> contacto&#64;altairparfum.com</p>
                <p>💬 <strong>WhatsApp:</strong> +57 320 340 5747</p>
                <p>📍 <strong>Ubicación:</strong> Bogotá D.C., Colombia</p>
              </div>
            </section>

          </div>

          <div class="mt-12 pt-6 border-t border-white/10 flex justify-between items-center">
            <a routerLink="/privacidad" class="text-amethyst-glint hover:text-white text-sm transition">
              Siguiente: Política de Privacidad →
            </a>
            <a routerLink="/tienda" class="bg-violet-600 hover:bg-violet-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition">
              Volver a la Tienda
            </a>
          </div>

        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsConditionsComponent {
  printPage() {
    window.print();
  }
}
